import React, { useEffect, useState } from "react";
import { ClipboardList } from "lucide-react";
import {
  guestQuestionsService,
  EventQuestion,
  GuestAnswerItem,
} from "../../services/guestQuestionsService";
import LoadingLogoSpinner from "../CustomComponents/LoadingLogoSpinner";

interface GuestAnswersViewProps {
  eventId: number;
  guestId: number;
  /** true = use the host endpoint; false = use the guest endpoint */
  isHost?: boolean;
}

export default function GuestAnswersView({
  eventId,
  guestId,
  isHost = false,
}: GuestAnswersViewProps) {
  const [questions, setQuestions] = useState<EventQuestion[]>([]);
  const [answers, setAnswers] = useState<GuestAnswerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    Promise.all([
      guestQuestionsService.getQuestionsForGuest(eventId, controller.signal),
      isHost
        ? guestQuestionsService.getGuestAnswersByHost(
            eventId,
            guestId,
            controller.signal,
          )
        : guestQuestionsService.getGuestAnswers(
            eventId,
            guestId,
            controller.signal,
          ),
    ])
      .then(([qs, ans]) => {
        const sorted = [...(qs ?? [])].sort((a, b) => a.order - b.order);
        setQuestions(sorted);
        setAnswers(ans ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError")
          return;
        setError("Failed to load answers.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [eventId, guestId, isHost]);

  const questionMap = questions.reduce<Record<number, (typeof questions)[0]>>(
    (acc, q) => { acc[q.ID] = q; return acc; },
    {}
  );

  const answerMap = answers.reduce<Record<number, string>>((acc, a) => {
    let display = a.answer;
    const q = questionMap[a.questionId];
    if (q?.type === "checkbox" && (!q.options || q.options.length === 0)) {
      if (a.answer === "" || a.answer === null || a.answer === undefined) {
        display = "";
      } else {
        display = a.answer === "true" ? "Yes" : "No";
      }
    } else {
      try {
        const parsed = JSON.parse(a.answer);
        if (Array.isArray(parsed)) display = parsed.join(", ");
      } catch {
        // not JSON — use as-is
      }
    }
    acc[a.questionId] = display;
    return acc;
  }, {});

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      {/* Scrollable answers list — height driven by flex parent (drawer), not viewport */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-1 pb-4"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {loading && (
          <div className="flex justify-center items-center h-40">
            <LoadingLogoSpinner small loading />
          </div>
        )}

        {!loading && error && (
          <p className="text-center text-sm text-red-500 py-10">{error}</p>
        )}

        {!loading && !error && questions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <ClipboardList size={36} className="text-[#c4a0ac] mb-3" />
            <p className="text-sm text-[#786469]">No questions for this event.</p>
          </div>
        )}

        {!loading && !error && questions.length > 0 && (
          <div className="flex flex-col gap-3">
            {questions.map((q, idx) => {
              const answer = answerMap[q.ID];
              return (
                <div
                  key={q.ID}
                  className="bg-[#FAF2F6] rounded-2xl p-4 border border-[#ecdde4]"
                >
                  <p className="text-xs text-[#786469] mb-1.5">
                    {idx + 1}. {q.label}
                    {q.isRequired && (
                      <span className="text-red-400 ml-0.5">*</span>
                    )}
                  </p>
                  {answer !== undefined && answer !== "" ? (
                    <p className="text-sm font-medium text-[#0F090B] break-words">
                      {answer}
                    </p>
                  ) : (
                    <p className="text-sm text-[#bbb] italic">No answer provided</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
